import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Moment from 'moment';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  RadioGroup,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { dangerColor } from 'assets/jss/material-dashboard-pro-react';
import DATA_RANGE_FORMAT from 'constants/dataRangeFormat';
import Button from 'components/CustomButtons/Button';
import DateRange from 'components/DateRange/DateRange';
import FormTextInput from 'components/FormTextInput/FormTextInput';
import LabeledCheckbox from 'components/Checkbox/LabeledCheckbox';
import LabeledRadio from 'components/LabeledRadio/LabeledRadio';

import DEFAULT_MULTIPLIER from '../../constants/defaultMultiplier';

const MULTIPLIER_TYPES = {
  individual: 'individual',
  shared: 'shared',
};

const useStyles = makeStyles({
  dialogPaper: {
    overflowY: 'visible',
  },
  dialogContent: {
    overflowY: 'visible',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'nowrap',
    marginBottom: '16px',
  },
  multiplierInfo: { marginBottom: '16px', fontSize: '14px', fontWeight: '400' },
  labeledCheckbox: {
    marginBottom: '10px',
  },
  firstRange: {
    marginBottom: '16px',
  },
  error: {
    margin: '12px 0 0',
    fontSize: '14px',
    color: dangerColor[0],
  },
});

const MultiplierModal = ({
  isOpen,
  onClose,
  onIndividualMultiplierSubmit,
  onSharedMultiplierSubmit,
}) => {
  const classes = useStyles();

  const { t } = useTranslation();

  const [multiplierType, setMultiplierType] = useState(
    MULTIPLIER_TYPES.individual
  );

  const [multiplier, setMultiplier] = useState(DEFAULT_MULTIPLIER);

  const [isDataRangeActive, setIsDataRangeActive] = useState(false);

  const [range, setRange] = useState([undefined, undefined]);

  const [firstRange, setFirstRange] = useState([undefined, undefined]);
  const [secondRange, setSecondRange] = useState([undefined, undefined]);

  const [rangeFrom, rangeTo] = range;
  const [firstRangeFrom, firstRangeTo] = firstRange;
  const [secondRangeFrom, secondRangeTo] = secondRange;

  const isRangeFilled = !!(rangeFrom && rangeTo);
  const isFirstRangeFilled = !!(firstRangeFrom && firstRangeTo);
  const isSecondRangeFilled = !!(secondRangeFrom && secondRangeTo);

  const getDateRangeErrorMessage = ([dateFrom, dateTo]) => {
    if (dateFrom && dateTo) {
      // eslint-disable-next-line new-cap -- FIXME
      const dateFromMomentObj = Moment(dateFrom, DATA_RANGE_FORMAT);
      // eslint-disable-next-line new-cap -- FIXME
      const dateToMomentObj = Moment(dateTo, DATA_RANGE_FORMAT);

      if (!dateFromMomentObj.isBefore(dateToMomentObj)) {
        return t('reports.dateFromLowerThanDateToError');
      }
    }

    return '';
  };

  const getDataRangesErrorMessage = () => {
    if (isDataRangeActive && isFirstRangeFilled && isSecondRangeFilled) {
      // eslint-disable-next-line new-cap -- FIXME
      const firstRangeFromMomentObj = Moment(firstRangeFrom, DATA_RANGE_FORMAT);
      // eslint-disable-next-line new-cap -- FIXME
      const firstRangeToMomentObj = Moment(firstRangeTo, DATA_RANGE_FORMAT);
      // eslint-disable-next-line new-cap -- FIXME
      const secondRangeFromMomentObj = Moment(
        secondRangeFrom,
        DATA_RANGE_FORMAT
      );
      // eslint-disable-next-line new-cap -- FIXME
      const secondRangeToMomentObj = Moment(secondRangeTo, DATA_RANGE_FORMAT);

      const firstRangeDaysDiff = firstRangeFromMomentObj.diff(
        firstRangeToMomentObj,
        'days'
      );
      const secondRangeDaysDiff = secondRangeFromMomentObj.diff(
        secondRangeToMomentObj,
        'days'
      );

      if (firstRangeDaysDiff !== secondRangeDaysDiff) {
        return t('reports.dateRangeEqualityError');
      }
    }

    return '';
  };

  const dateRangeErrorMessage = getDateRangeErrorMessage(range);
  const firstDateRangeErrorMessage = getDateRangeErrorMessage(firstRange);
  const secondDateRangeErrorMessage = isDataRangeActive
    ? getDateRangeErrorMessage(secondRange)
    : '';
  const dataRangesErrorMessage = getDataRangesErrorMessage();

  return (
    <Dialog
      classes={{
        paper: classes.dialogPaper,
      }}
      fullWidth={true}
      open={isOpen}
      onClose={onClose}
    >
      <DialogTitle>{t('reports.setMultiplier')}</DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <RadioGroup
          className={classes.radioGroup}
          value={multiplierType}
          onChange={e => {
            setMultiplierType(e.target.value);
          }}
        >
          <LabeledRadio
            label={t('reports.individualMultiplier')}
            value={MULTIPLIER_TYPES.individual}
          />
          <LabeledRadio
            label={t('reports.sharedMultiplier')}
            value={MULTIPLIER_TYPES.shared}
          />
        </RadioGroup>
        <p className={classes.multiplierInfo}>
          {multiplierType === MULTIPLIER_TYPES.individual
            ? t('reports.multiplierComputingIndividual', {
                multiplier: DEFAULT_MULTIPLIER,
              })
            : t('reports.multiplierComputingShared')}
        </p>
        {multiplierType === MULTIPLIER_TYPES.individual ? (
          <>
            <LabeledCheckbox
              className={classes.labeledCheckbox}
              label={t('reports.computeBasedOnDateRanges')}
              checked={isDataRangeActive}
              onChange={e => {
                setIsDataRangeActive(e.target.checked);
              }}
            />
            {isDataRangeActive ? (
              <>
                <DateRange
                  className={classes.firstRange}
                  title={t('reports.rangeI')}
                  dataRange={firstRange}
                  onDateRangeChange={setFirstRange}
                  errorMessage={firstDateRangeErrorMessage}
                  labelFrom={t('reports.dateFrom')}
                  labelTo={t('reports.dateTo')}
                />
                <DateRange
                  title={t('reports.rangeII')}
                  dataRange={secondRange}
                  onDateRangeChange={setSecondRange}
                  errorMessage={secondDateRangeErrorMessage}
                  labelFrom={t('reports.dateFrom')}
                  labelTo={t('reports.dateTo')}
                />
                {dataRangesErrorMessage && (
                  <p className={classes.error}>{dataRangesErrorMessage}</p>
                )}
              </>
            ) : (
              <DateRange
                dataRange={range}
                onDateRangeChange={setRange}
                errorMessage={dateRangeErrorMessage}
                labelFrom={t('reports.dateI')}
                labelTo={t('reports.dateII')}
              />
            )}
          </>
        ) : (
          <FormTextInput
            name="multiplier"
            label={t('common.multiplier')}
            type="number"
            value={multiplier}
            onChange={e => {
              setMultiplier(e.target.value);
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.shared.cancel')}</Button>
        <Button
          color="success"
          onClick={() => {
            if (multiplierType === MULTIPLIER_TYPES.individual) {
              if (isDataRangeActive) {
                if (
                  firstDateRangeErrorMessage ||
                  secondDateRangeErrorMessage ||
                  dataRangesErrorMessage
                ) {
                  return;
                }

                if (!isFirstRangeFilled || !isSecondRangeFilled) {
                  toast.error(t('reports.multiplierDate'));
                  return;
                }

                onIndividualMultiplierSubmit({
                  firstRange,
                  secondRange,
                });
              } else {
                if (dateRangeErrorMessage) {
                  return;
                }

                if (!isRangeFilled) {
                  toast.error(t('reports.multiplierDate'));
                  return;
                }

                onIndividualMultiplierSubmit({
                  firstRange: [rangeFrom, rangeFrom],
                  secondRange: [rangeTo, rangeTo],
                });
              }
            } else {
              onSharedMultiplierSubmit(multiplier);
            }
          }}
        >
          {t('common.shared.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MultiplierModal;
